// frontend/src/farm/farmAnimals.mjs — the client mirror of backend farm_animals.py (spec
// 2026-09-29-farm-4c-animals-design.md): chickens and cows in huts; waiting ones frozen in
// farm.waiting_animals. Pure; pinned to the server by the parity fixture.
const CLOCKS = ['last_fed_iso', 'last_happiness_iso', 'last_produced_iso', 'last_collected_iso'];
let localIds = 0;
const reject = (reason) => ({ ok: false, reason });
const objects = (s) => s.catalog.farm.objects ?? {};

export const farmAnimals = (s) => (s.animals ?? []).filter((a) => a.scene === 'farm');
export const queueOf = (s, species) => (((s.farm.waiting_animals ??= {})[species]) ??= []);
export const waitingCount = (s, species) => (s.farm.waiting_animals?.[species] ?? []).length;
const huts = (s, species) => (s.placements ?? []).filter((p) => p.scene === 'farm' && p.type === 'hut' && objects(s)[p.item]?.animal === species);
export const residents = (s, hutId) => farmAnimals(s).filter((a) => a.buda_placement_id === hutId);
const free = (s, hut) => (objects(s)[hut.item]?.holds ?? 0) - residents(s, hut.id).length;
export const hutOf = (s, a) => (s.placements ?? []).find((p) => p.id === a.buda_placement_id) ?? null;

function freeze(rec, nowMs) {
  const { buda_placement_id: _, ...out } = rec;
  return { ...out, waiting_since_iso: new Date(nowMs).toISOString() };
}
function thaw(rec, hutId, nowMs) {
  const { waiting_since_iso: since, ...out } = rec;
  if (since) {
    const waited = nowMs - Date.parse(since);
    for (const k of CLOCKS) if (out[k]) out[k] = new Date(Date.parse(out[k]) + waited).toISOString();
  }
  return { ...out, scene: 'farm', buda_placement_id: hutId };
}
export function releaseInto(s, hut, nowMs) {
  const q = queueOf(s, objects(s)[hut.item].animal);
  let n = 0;
  while (q.length && free(s, hut) > 0) { (s.animals ??= []).push(thaw(q.shift(), hut.id, nowMs)); n++; }
  return n;
}
export function sendBack(s, hut, nowMs) {
  const home = residents(s, hut.id);
  if (!home.length) return;
  queueOf(s, objects(s)[hut.item].animal).unshift(...home.map((a) => freeze(a, nowMs)));
  const ids = new Set(home.map((a) => a.id));
  s.animals = s.animals.filter((a) => !ids.has(a.id));
}
function newRecord(species, nowMs) {
  const iso = new Date(nowMs).toISOString();
  return { id: `local-a${++localIds}`, type: species, scene: 'farm', happiness: 80, pending_count: 0,
    last_happiness_iso: iso, last_fed_iso: iso, last_produced_iso: iso, last_collected_iso: iso };
}
const find = (s, id) => farmAnimals(s).find((a) => a.id === id);

export function applyAnimalOp(s, op, nowMs) {
  const fc = s.catalog.farm; const rules = fc.animal_rules ?? {};
  if (op.op === 'buy_animal') {
    const spec = fc.animals?.[op.item];
    if (!spec) return reject('FARM_UNKNOWN_ANIMAL');
    if ((s.level ?? 1) < objects(s)[spec.hut].level) return reject('FARM_LEVEL_TOO_LOW');
    if (s.money < spec.price) return reject('INSUFFICIENT_MONEY_FARM');
    s.money -= spec.price;
    const rec = newRecord(op.item, nowMs);
    const home = huts(s, op.item).find((h) => free(s, h) > 0);
    if (home) (s.animals ??= []).push({ ...rec, buda_placement_id: home.id });
    else queueOf(s, op.item).push(freeze(rec, nowMs));
    return { ok: true };
  }
  const a = find(s, op.id);
  if (!a) return reject('ANIMAL_NOT_FOUND');
  const iso = new Date(nowMs).toISOString();
  if (op.op === 'collect_animal') {
    const n = Number(a.pending_count ?? 0);
    if (n <= 0) return reject('NO_PENDING_PRODUCT');
    const prod = fc.animals[a.type].produces;
    (s.inventory.products ??= {})[prod] = (s.inventory.products[prod] ?? 0) + n;
    // A full stack's clock stood still, so it starts again now; otherwise the time towards the next product
    // is kept (farm_animals.collect, owner 2026-10-31).
    if (n >= (rules.stack_max ?? 6)) a.last_produced_iso = iso;
    a.pending_count = 0; a.last_collected_iso = iso;
    return { ok: true, count: n, product: prod };
  }
  if (op.op === 'feed_animal') {
    if (!isHungry(a, nowMs, rules.feed_hours)) return reject('ANIMAL_NOT_HUNGRY');   // owner 2026-09-30
    const food = fc.animals[a.type].food_id;
    if ((s.inventory.foods?.[food] ?? 0) <= 0) return reject('NO_FOOD');
    s.inventory.foods[food] -= 1;
    a.last_fed_iso = iso; a.last_happiness_iso = iso; a.happiness = Math.min(100, a.happiness + (rules.per_feed ?? 40));
    return { ok: true };
  }
  if ((a.happiness ?? 0) >= (rules.pet_below ?? 80)) return { ok: false, reason: 'ANIMAL_HAPPY' };
  a.happiness = 100; a.last_happiness_iso = iso;   // pet_animal: filled up, only below 80 (owner 2026-09-30)
  return { ok: true };
}

// When the next egg / milk is due on this farm (epoch ms), from the server's derived fields:
// last_produced_iso + current_production_interval_seconds (null while hungry or the stack is
// full). Absolute, so it stays right between syncs; the scene refreshes once it has passed.
export function nextProductAtMs(animals) {
  let best = null;
  for (const a of animals ?? []) {
    if (a.scene !== 'farm' || a.current_production_interval_seconds == null || !a.last_produced_iso) continue;
    const at = Date.parse(a.last_produced_iso) + a.current_production_interval_seconds * 1000;
    if (best === null || at < best) best = at;
  }
  return best;
}

// Hungry a farm day, 23 h (catalog animal_rules.feed_hours), after its last meal — the server's rule
// (farm_animals.is_hungry): only then may it be fed (owner 2026-09-30; 23 h since 2026-10-31). No readable meal: hungry.
export function isHungry(a, nowMs, feedHours = 23) {
  const fed = Date.parse(a?.last_fed_iso ?? '');
  return Number.isNaN(fed) || nowMs - fed > (feedHours ?? 23) * 3600e3;
}
